import { Trans, useTranslation } from "react-i18next";

import { PatientJournalsTableContent } from "../../../../@types/Patient";
import { ReactComponent as NoJournalsImage } from "../../../../assets/images/man-reading.svg";
import { InfiniteTable } from "../../../../components/InfiniteTable";
import { RetryButton } from "../../../../components/RetryButton";
import { Table } from "../../../../components/Table";
import { TableSkeleton } from "../../../../components/TableSkeleton";
import { useModal } from "../../../../hooks/useModal";
import { mixpanelActions } from "../../../../utils/mixpanel";
import { desktopTableColumns } from "../JournalsSection/patientJournalsTableColumns";
import { ViewJournalModal } from "./components/ViewJournalModal";
import {
  ImageContainer,
  JournalsTableContainer,
  JournalsTableInnerContainer,
  NoContentMessage,
} from "./PatientJournalsTable.style";

type PatientJournalsTableProps = {
  content: PatientJournalsTableContent[];
  isLoading?: boolean;
  isError?: boolean;
  highlightedRows: boolean[];
  refreshTable: () => void;
  // Infinite scroll props (optional for backward compatibility)
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  fetchNextPage?: () => void;
  useInfiniteScroll?: boolean;
};

export function PatientJournalsTable({
  isLoading,
  isError,
  content,
  highlightedRows,
  refreshTable,
  hasNextPage = false,
  isFetchingNextPage = false,
  fetchNextPage,
  useInfiniteScroll = false,
}: PatientJournalsTableProps) {
  const { t } = useTranslation();

  const { openModal } = useModal();

  const handleRowClick = (
    patientJournalContent: PatientJournalsTableContent
  ) => {
    mixpanelActions.track(`User Action: ClickedJournalEntry`);
    openModal(<ViewJournalModal journalId={patientJournalContent.id} />, {
      width: "100%",
      height: "450px",
      showCloseButton: true,
    });
  };

  if (isLoading) {
    return (
      <JournalsTableContainer style={{ overflow: "hidden" }}>
        <JournalsTableInnerContainer style={{ overflow: "hidden" }}>
          <TableSkeleton columnsNumber={4} rowsNumber={8} />
        </JournalsTableInnerContainer>
      </JournalsTableContainer>
    );
  }

  if (isError) {
    return (
      <ImageContainer>
        <p>
          <Trans
            i18nKey="patientPage_journals_loadError"
            components={{
              retry: <RetryButton onClick={refreshTable} />,
            }}
          />
        </p>
      </ImageContainer>
    );
  }

  if (content && content.length > 0) {
    // Use InfiniteTable when infinite scroll is enabled
    if (useInfiniteScroll && fetchNextPage) {
      return (
        <JournalsTableContainer>
          <JournalsTableInnerContainer>
            <InfiniteTable
              data={content}
              columns={desktopTableColumns}
              highlightedRows={highlightedRows}
              onRowClick={handleRowClick}
              highlightColor="rgba(233, 230, 244, .48)"
              hasNextPage={hasNextPage}
              isFetchingNextPage={isFetchingNextPage}
              fetchNextPage={fetchNextPage}
              skeletonColumns={4}
              skeletonRows={8}
            />
          </JournalsTableInnerContainer>
        </JournalsTableContainer>
      );
    }

    // Default to regular Table for backward compatibility
    return (
      <JournalsTableContainer>
        <JournalsTableInnerContainer>
          <Table
            data={content}
            columns={desktopTableColumns}
            highlightedRows={highlightedRows}
            onRowClick={handleRowClick}
            highlightColor="rgba(233, 230, 244, .48)"
          />
        </JournalsTableInnerContainer>
      </JournalsTableContainer>
    );
  }

  return (
    <ImageContainer>
      <NoJournalsImage width={181} height={158} />
      <NoContentMessage>{t("patientPage_journal_empty")}</NoContentMessage>
    </ImageContainer>
  );
}
